// src/store/modules/shareCandidates.js
import api from '@/api/api'

export default {
    namespaced: true,
    state: {
        shareCandidates: [],
        currentShareCandidate: null,
        loading: false,
        error: null
    },
    getters: {
        allShareCandidates: (state) => state.shareCandidates,
        currentShareCandidate: (state) => state.currentShareCandidate,
        loading: (state) => state.loading,
        error: (state) => state.error
    },
    mutations: {
        SET_SHARE_CANDIDATES(state, shareCandidates) {
            state.shareCandidates = shareCandidates
        },
        SET_CURRENT_SHARE_CANDIDATE(state, shareCandidate) {
            state.currentShareCandidate = shareCandidate
        },
        ADD_SHARE_CANDIDATE(state, shareCandidate) {
            state.shareCandidates.push(shareCandidate)
        },
        UPDATE_SHARE_CANDIDATE(state, updatedShareCandidate) {
            const index = state.shareCandidates.findIndex(sc => sc._id === updatedShareCandidate._id)
            if (index !== -1) {
                state.shareCandidates.splice(index, 1, updatedShareCandidate)
            }
        },
        REMOVE_SHARE_CANDIDATE(state, id) {
            state.shareCandidates = state.shareCandidates.filter(sc => sc._id !== id)
        },
        SET_LOADING(state, status) {
            state.loading = status
        },
        SET_ERROR(state, error) {
            state.error = error
        },
        CLEAR_ERROR(state) {
            state.error = null
        }
    },
    actions: {
        // Create a new share candidate
        async createShareCandidate({ commit }, data) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.post('/share-candidates', data)

                commit('ADD_SHARE_CANDIDATE', response.data.data)
                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to create share candidate'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Fetch all share candidates for a talent
        async fetchShareCandidatesByTalent({ commit }, talentId) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.get(`/share-candidates/talent/${talentId}`)

                commit('SET_SHARE_CANDIDATES', response.data.data)
                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch share candidates'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Fetch a single share candidate by ID (for admin)
        async fetchShareCandidateById({ commit }, id) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.get(`/share-candidates/${id}`)

                commit('SET_CURRENT_SHARE_CANDIDATE', response.data.data)
                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch share candidate'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Fetch a single share candidate by ID (public)
        async fetchPublicShareCandidateById({ commit }, id) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.get(`/public/share-candidates/${id}`)

                commit('SET_CURRENT_SHARE_CANDIDATE', response.data.data)
                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch share candidate'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Update a share candidate
        async updateShareCandidate({ commit }, { id, data }) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.put(`/share-candidates/${id}`, data)

                commit('UPDATE_SHARE_CANDIDATE', response.data.data)
                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to update share candidate'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Delete a share candidate
        async deleteShareCandidate({ commit }, id) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                await api.delete(`/share-candidates/${id}`)

                commit('REMOVE_SHARE_CANDIDATE', id)
                return true
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to delete share candidate'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Request an introduction to a candidate
        async requestIntroduction({ commit }, { id, data }) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.post(`/public/share-candidates/${id}/request-intro`, data)

                return response.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to request introduction'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        }
    }
}
